import React, { useCallback, useEffect, useRef, useState } from "react";
import { isExpired as isTokenExpired } from "../jwtUtils";

type SessionExpiryGuardProps = {
  jwt: string;
  onExpired: () => void;
};

/**
 * Refuses to render the authenticated subtree once the session token's `exp` has
 * passed, and signals expiry exactly once so the session can be torn down.
 *
 * This restores the backstop that `LogoutOnExpire` provided before the OIDC
 * migration (25b279be5). Its job is not renewal — that is TokenRenewalManager's —
 * but to guarantee that when renewal has failed or been declined, the session and
 * everything mounted inside it stops. Components below this boundary hold polling
 * intervals; unmounting them is what clears those intervals, so the guard must
 * wrap the subtree rather than sit beside it.
 */
export const SessionExpiryGuard: React.FC<SessionExpiryGuardProps> = ({
  jwt,
  onExpired,
  children,
}) => {
  // Unreadable, absent, or non-finite expiry fails closed inside isTokenExpired:
  // a token we cannot reason about must not keep an authenticated subtree alive.
  const isExpired = useCallback((): boolean => isTokenExpired(jwt), [jwt]);

  const [expired, setExpired] = useState(isExpired);
  const signalledRef = useRef(false);

  useEffect(() => {
    if (!expired) {
      return;
    }

    if (signalledRef.current) {
      return;
    }

    signalledRef.current = true;
    onExpired();
  }, [expired, onExpired]);

  useEffect(() => {
    // Re-evaluated whenever the token changes, so a renewal that swaps in a
    // fresh token revives the subtree and re-arms the expiry signal.
    const check = () => {
      const nowExpired = isExpired();
      setExpired(nowExpired);
      if (!nowExpired) {
        signalledRef.current = false;
      }
    };

    check();

    // A timer alone is not enough: one scheduled before the machine suspends is
    // not guaranteed to fire on resume, which is precisely the case that produced
    // tokens arriving 20 hours past expiry. Re-check whenever the tab is shown.
    const interval = window.setInterval(check, 1000);
    document.addEventListener("visibilitychange", check);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", check);
    };
  }, [isExpired]);

  return <>{!expired && children}</>;
};
